// The app shell: a left sidebar (Mira wordmark · icon area nav · signed-in
// identity footer) + the routed content area. Mounted as the root route's
// component, so every page renders inside it. The nav lists ONLY the areas the
// signed-in employee is granted (GET /api/admin/permissions/me); an employee
// with zero grants still sees Home.
//
// Responsive: full rail on wide screens, an icon rail ≤1024px (labels hidden,
// `title` carries them), and off-canvas ≤720px (a topbar hamburger opens the
// full sidebar over a scrim). All breakpoint behavior lives in globals.css; the
// only state here is the off-canvas open flag.
import { useState } from "react";
import { Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { IconButton } from "@recruiter/shared";

import { IS_PROD } from "../config";
import { createAdminDebugNote } from "../lib/api";
import { visibleAreas } from "../lib/areas";
import { captureViewport } from "../lib/debug-screenshot";
import { usePersistedState } from "../lib/persisted-state";
import { meQuery, myAreasQuery } from "../lib/queries";
import { signOutAdmin } from "../lib/sign-out";
import { useTheme } from "../lib/theme-client";
import { DebugNotePanel } from "./debug-note-panel";
import brandMira from "../assets/brand-mira.png";
import { AREA_ART, AreaArtIcon } from "./area-art";
import { CloseIcon, HomeIcon, MenuIcon, MoonIcon, SignOutIcon, SunIcon } from "./icons";
import { ViewStateScope } from "./view-state-scope";

/** The product wordmark: the Mira mascot portrait + serif "Mira" over an
 *  uppercase descriptor — same editorial serif treatment as the buyer app. */
export function Wordmark() {
  return (
    <div className="ad-brand">
      <span className="ad-brand__mark" aria-hidden="true">
        <img src={brandMira} alt="" role="presentation" />
      </span>
      <span className="ad-brand__text">
        <span className="ad-brand__name">Mira</span>
        <span className="ad-brand__sub">Admin</span>
      </span>
    </div>
  );
}

/** Light/dark flip — Mira is light-first; the choice persists via theme-client
 *  and the pre-paint script restores it on the next load. */
function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <IconButton
      variant="ghost"
      tone="neutral"
      size="sm"
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
      onClick={toggle}
    >
      {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </IconButton>
  );
}

/** Log a debug note from wherever the tester is: capture the viewport
 *  (best-effort; a failed/timed-out shot still saves the note) and post it with
 *  the current admin route so the reviewer knows which page it is about. */
async function submitDebugNote(note: string): Promise<void> {
  const screenshot = await captureViewport();
  await createAdminDebugNote({
    note,
    page: window.location.pathname || null,
    screenshot,
  });
}

export function AppShell() {
  const me = useQuery(meQuery);
  const areas = useQuery(myAreasQuery);
  const [navOpen, setNavOpen] = useState(false);
  // The tester Debug toggle, ported from the buyer SPA's red topbar pill.
  // Persisted so debug mode survives a reload mid-testing-session; withheld in
  // real production (the server 404s the capture route there too).
  const [debugStored, setDebugMode] = usePersistedState("recruiter_admin_app_debug_mode", false);
  const debugMode = IS_PROD ? false : debugStored;
  const granted = areas.data ?? [];
  const navAreas = visibleAreas(granted);
  const email = me.data?.email ?? null;
  const closeNav = () => setNavOpen(false);

  return (
    <div className="ad-shell">
      {/* Mobile-only topbar (≤720px): hamburger + wordmark. */}
      <header className="ad-topbar">
        <IconButton
          variant="ghost"
          tone="neutral"
          aria-label="Open navigation"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Wordmark />
      </header>

      {navOpen ? (
        <button
          type="button"
          className="ad-scrim"
          aria-label="Close navigation"
          onClick={closeNav}
        />
      ) : null}

      <aside className="ad-sidebar" data-open={navOpen || undefined}>
        <div className="ad-sidebar__head">
          <Wordmark />
          <IconButton
            className="ad-sidebar__close"
            variant="ghost"
            tone="neutral"
            aria-label="Close navigation"
            onClick={closeNav}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <nav className="ad-nav" aria-label="Admin areas">
          <Link
            to="/"
            className="ad-nav__link"
            activeOptions={{ exact: true }}
            activeProps={{ "aria-current": "page" }}
            title="Home"
            onClick={closeNav}
          >
            <span className="ad-nav__icon">
              <HomeIcon />
            </span>
            <span className="ad-nav__label">Home</span>
          </Link>
          {navAreas.map((area) => {
            // The row's padding follows the icon it will ACTUALLY render, so an
            // area added without a portrait keeps the line-icon spacing instead
            // of a portrait-sized gap.
            //
            // `system_admin` additionally takes `--meta`: it administers the
            // console rather than the product, so it stays below a rule — a
            // different level of the hierarchy, not a fourth product area. It
            // carries a portrait like the rest now (an ADMIN cap + key), so the
            // rule is the ONLY thing left marking that split. Don't drop it.
            const classes = ["ad-nav__link"];
            if (AREA_ART[area.key]) classes.push("ad-nav__link--art");
            if (area.key === "system_admin") classes.push("ad-nav__link--meta");
            return (
              <Link
                key={area.key}
                to={area.path}
                className={classes.join(" ")}
                activeProps={{ "aria-current": "page" }}
                title={area.label}
                onClick={closeNav}
              >
                <AreaArtIcon area={area.key} />
                <span className="ad-nav__label">{area.label}</span>
              </Link>
            );
          })}

          {/* Same sub-row, same reason: the experiment register is a page under
              `system_admin`, not an area of its own. */}
          {navAreas.some((area) => area.key === "system_admin") ? (
            <Link
              to="/experiments"
              className="ad-nav__link ad-nav__link--sub"
              activeProps={{ "aria-current": "page" }}
              title="Experiments"
              onClick={closeNav}
            >
              <span className="ad-nav__label">Experiments</span>
            </Link>
          ) : null}
        </nav>

        <div className="ad-sidebar__footer">
          <div className="ad-sidebar__prefs">
            <ThemeToggle />
            {!IS_PROD ? (
              <button
                type="button"
                className="debug-toggle"
                data-on={debugMode || undefined}
                aria-pressed={debugMode}
                title={
                  debugMode
                    ? "Turn off debug mode"
                    : "Debug mode: log tester notes with a screenshot from any page"
                }
                onClick={() => setDebugMode(!debugMode)}
              >
                <span className="debug-toggle__dot" aria-hidden="true" />
                Debug
              </button>
            ) : null}
          </div>
          {email ? (
            <div className="ad-whoami">
              <span className="ad-whoami__avatar" aria-hidden="true">
                {email.charAt(0).toUpperCase()}
              </span>
              <span className="ad-whoami__meta">
                <span className="ad-whoami__label">Signed in as</span>
                <span className="ad-whoami__email" title={email}>
                  {email}
                </span>
              </span>
              <IconButton
                variant="ghost"
                tone="neutral"
                size="sm"
                aria-label="Sign out"
                title="Sign out"
                onClick={() => signOutAdmin()}
              >
                <SignOutIcon size={18} />
              </IconButton>
            </div>
          ) : null}
        </div>
      </aside>

      <main className="ad-content">
        {/* Wraps the page, not the shell: the sidebar holds no view state, and
            every table that restores one is inside the outlet. */}
        <ViewStateScope>
          <Outlet />
        </ViewStateScope>
      </main>

      {/* The universal debug-note box: floats over every page while debug mode
          is on, so a tester can log a note from wherever the bug is. */}
      {debugMode ? (
        <DebugNotePanel onSubmit={submitDebugNote} onTurnOff={() => setDebugMode(false)} />
      ) : null}
    </div>
  );
}
